/**
 * The list of menus and sub-menus an operator can grant a role.
 *
 * Derived from the nav schemas themselves rather than written out again. A
 * second hand-maintained copy of the sidebar is a copy that drifts: somebody
 * adds a screen, the sidebar grows it, and the permission editor never offers
 * it — so the menu is ungrantable and looks like a bug in RBAC.
 *
 * The union of all three role schemas, because a role's menus are edited before
 * anyone decides which dashboard that role lands on, and a custom role can be
 * pointed at any of them.
 */
import {
  EMPLOYEE_NAV_SCHEMA,
  SECTION_BY_ID,
  getAdminNavSchema,
  getManagerNavSchema,
} from './navigation';

/**
 * [{ id, label, section, children: [{ id, label }] }] — two levels, which is all
 * the sidebar renders. A leaf with no children is its own entry.
 */
export const buildMenuCatalog = () => {
  const byId = new Map();
  const order = [];
  // Half a dozen ids sit under a group in one schema and at the top level in
  // another — `attendance` is a tab of Attendance for an admin and a menu of its
  // own for an employee, and the HR-manager extras are the same screens the
  // admin console files under HR Operations. Listed both ways they appear twice
  // in the editor, and ticking one copy does not tick the other, so the operator
  // saves a grant that half-applies. The grouped placement wins.
  const asChild = new Set();

  const claimAsChild = (id) => {
    asChild.add(id);
    if (byId.has(id)) {
      byId.delete(id);
      const at = order.indexOf(id);
      if (at !== -1) order.splice(at, 1);
    }
  };

  const add = (item) => {
    (item.subItems || []).forEach((sub) => claimAsChild(sub.id));
    if (asChild.has(item.id)) return;
    if (!byId.has(item.id)) {
      order.push(item.id);
      byId.set(item.id, {
        id: item.id,
        label: item.label,
        section: SECTION_BY_ID[item.id] || 'Other',
        children: [],
      });
    }
    const node = byId.get(item.id);
    (item.subItems || []).forEach((sub) => {
      if (!node.children.some((c) => c.id === sub.id)) {
        node.children.push({ id: sub.id, label: sub.label });
      }
    });
  };

  [
    // Admin first: it is the most grouped of the three, so its filing is the one
    // the others get merged into rather than the other way round.
    getAdminNavSchema(),
    getManagerNavSchema({ isHrManager: true }),
    getManagerNavSchema(),
    EMPLOYEE_NAV_SCHEMA,
  ].forEach((schema) => schema.forEach(add));

  return order.map((id) => byId.get(id));
};

/** Every grantable id, parents included — a parent id is what the sidebar shows. */
export const allMenuIds = (catalog = buildMenuCatalog()) =>
  catalog.flatMap((group) => [group.id, ...group.children.map((c) => c.id)]);

/**
 * Ticking a sub-menu has to tick its parent, or the group it lives in never
 * renders and the grant is silently inert. Unticking the last child drops the
 * parent for the same reason.
 */
export const normaliseMenuSelection = (selected = [], catalog = buildMenuCatalog()) => {
  const chosen = new Set(selected);
  catalog.forEach((group) => {
    if (group.children.length === 0) return;
    const anyChild = group.children.some((c) => chosen.has(c.id));
    if (anyChild) chosen.add(group.id);
    else chosen.delete(group.id);
  });
  return [...chosen].sort();
};
